import { BasicButton, Stack, Text } from "@certn/cascadia";
import {
  Card,
  FileUpload,
  LinkButton,
  Space,
  Tooltip,
} from "@certn/cascadia-react";
import { EVENT_NAMES, useEventTracking } from "@certn/utils";
import { css, cx } from "@emotion/css";
import { useUser } from "../../../../../hooks/useUser";
import { PropsWithChildren, ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { CobrandingUrlsResponse, useBrandingContext } from "../BrandingContext";
import { darkColors, lightColors } from "@certn/cascadia-tokens";
import { rememberBrandAssetObjectUrl } from "./brandAssetObjectUrls";
import {
  BRAND_ASSET_BUCKET_DIRECTORY,
  sanitizeBrandAssetFileName,
} from "./sanitizeBrandAssetFileName";

const BRAND_ASSET_FILE_TYPES = ["image/png", "image/jpeg"];

type BrandAssetUploadSectionProps = PropsWithChildren<{
  label: ReactNode;
  existingUrl?: string;
  uploadTestId: string;
  removeTestId: string;
  showFileInfo?: "size" | "type";
  allowedFileTypes?: string[];
  fileTypes?: string[];
  maxSizeBytes?: number;
  isDisabled?: boolean;
  uploadKey:
    | "org_logo_url"
    | "org_icon_url"
    | "org_icon_url_dark_mode"
    | "org_logo_url_dark_mode";
}>;

const getFilenameFromUrl = (url: string): string => {
  try {
    return url.split("/").pop() || url;
  } catch {
    return url;
  }
};

export function BrandAssetUploadSection(props: BrandAssetUploadSectionProps) {
  const {
    label,
    existingUrl,
    uploadTestId,
    removeTestId,
    showFileInfo,
    allowedFileTypes = BRAND_ASSET_FILE_TYPES,
    fileTypes = BRAND_ASSET_FILE_TYPES,
    maxSizeBytes = 5000000,
    isDisabled,
    uploadKey,
  } = props;

  const { details: userDetails } = useUser();
  const auth0Name = userDetails?.client_account?.auth0_name || uuidv4();
  const { t } = useTranslation();
  const { trackEvent } = useEventTracking();
  const {
    setBrandingAssets,
    setAssetPreview,
    brandingAssets,
    isLoading,
    isSaving,
  } = useBrandingContext();

  const { processedBrandingAssets } = useBrandingContext();
  const resolvedExistingUrl =
    existingUrl ?? brandingAssets?.[uploadKey] ?? undefined;

  const handleRemove = () => {
    rememberBrandAssetObjectUrl(brandingAssets?.[uploadKey], null);
    setAssetPreview(uploadKey, null);
    setBrandingAssets({
      bucket: brandingAssets?.bucket ?? "",
      org_logo_url:
        uploadKey === "org_logo_url"
          ? null
          : brandingAssets?.org_logo_url ?? null,
      org_icon_url:
        uploadKey === "org_icon_url"
          ? null
          : brandingAssets?.org_icon_url ?? null,
      org_icon_url_dark_mode:
        uploadKey === "org_icon_url_dark_mode"
          ? null
          : brandingAssets?.org_icon_url_dark_mode ?? null,
      org_logo_url_dark_mode:
        uploadKey === "org_logo_url_dark_mode"
          ? null
          : brandingAssets?.org_logo_url_dark_mode ?? null,
      primary_brand_color: brandingAssets?.primary_brand_color ?? null,
      optimal_text_color: brandingAssets?.optimal_text_color ?? null,
      whitelabel_report: brandingAssets?.whitelabel_report ?? false,
    });
  };

  return (
    <Stack>
      {!isLoading && !resolvedExistingUrl && (
        <Stack
          gap="space-3"
          orientation="horizontal"
          justify="space-between"
          align="center"
        >
          <Text style="form/section-heading">{label}</Text>
          <div className={styles.uploadTrigger}>
            <FileUpload
              icon="cloud_upload"
              testID={uploadTestId}
              bucketDirectory={BRAND_ASSET_BUCKET_DIRECTORY}
              requestUrl="api/internal/client-portal/generate-presigned-url/"
              allowedFileTypes={allowedFileTypes}
              fileTypes={fileTypes}
              maxSizeBytes={maxSizeBytes}
              multiple={false}
              showFileInfo={showFileInfo}
              generateFileName={(file) =>
                sanitizeBrandAssetFileName({
                  auth0Name,
                  originalFileName: file.name,
                  publicBucketUrl: brandingAssets?.bucket ?? "",
                })
              }
              overwriteExistingFiles
              setFiles={(files) => {
                if (files.length > 0) {
                  const isLogo =
                    uploadKey === "org_logo_url" ||
                    uploadKey === "org_logo_url_dark_mode";
                  if (isLogo) {
                    trackEvent(EVENT_NAMES.CP_UPLOAD_LOGO, {
                      uploadLogo: true,
                    });
                  } else {
                    trackEvent(EVENT_NAMES.CP_UPLOAD_ICON, {
                      uploadIcon: true,
                    });
                  }
                }
                const file = files[0];
                rememberBrandAssetObjectUrl(
                  file?.key,
                  file?.url ?? null,
                  file?.file
                );
                setAssetPreview(uploadKey, file?.url ?? null);
                setBrandingAssets({
                  ...brandingAssets,
                  [uploadKey]: file?.key ?? null,
                } as CobrandingUrlsResponse);
              }}
              triggerButton={
                <BasicButton size="small" leftIcon="upload_file">
                  <Trans>Upload</Trans>
                </BasicButton>
              }
            />
          </div>
        </Stack>
      )}
      {!isLoading && resolvedExistingUrl && (
        <Stack
          gap="space-3"
          orientation="horizontal"
          justify="space-between"
          className={styles.section}
        >
          <Text style="form/section-heading">{label}</Text>
          <Card className={styles.card}>
            <Stack orientation="horizontal" gap="space-3" align="center">
              <img
                src={processedBrandingAssets?.[uploadKey] ?? ""}
                alt={t("Uploaded asset")}
                className={cx(
                  styles.image,
                  (uploadKey === "org_icon_url_dark_mode" ||
                    uploadKey === "org_logo_url_dark_mode") &&
                    styles.darkModeImage
                )}
              />
              <Tooltip
                testID={`${uploadKey}-filename-tooltip`}
                placement="top"
                description={getFilenameFromUrl(resolvedExistingUrl)}
              >
                <Text style="global/heading-medium" truncate>
                  {getFilenameFromUrl(resolvedExistingUrl)}
                </Text>
              </Tooltip>
              <Space />
              <LinkButton
                isDisabled={isDisabled || isSaving}
                onPress={() => handleRemove()}
                testID={removeTestId}
                textStyle="global/body-secondary-link"
                textColor="text-weak"
              >
                <Trans>Remove</Trans>
              </LinkButton>
            </Stack>
          </Card>
        </Stack>
      )}
    </Stack>
  );
}

const styles = {
  uploadTrigger: css({
    flexShrink: 0,
    width: "fit-content",
    marginLeft: "auto",
  }),
  section: css({ display: "flex", alignItems: "center", flexWrap: "wrap" }),
  card: css({
    paddingLeft: "var(--space-3)",
    paddingRight: "var(--space-3)",
    flexGrow: 1,
    maxWidth: "300px",

    "> div": css({ minHeight: "48px" }),
  }),
  image: css({
    backgroundColor: lightColors["surface-base"],
    width: "30px",
    maxHeight: "30px",
    padding: "var(--space-1)",
    borderRadius: "var(--border-radius-2)",
    aspectRatio: "1/1",
    objectFit: "contain",
    flexShrink: 0,
  }),
  darkModeImage: css({
    backgroundColor: darkColors["surface-base"],
  }),
  truncatedText: css({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    width: "50%",
  }),
};
